import { format } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Button } from "./button.tsx";
import { Calendar } from "./calendar.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.tsx";

type DateTimePickerProps = {
  value: Date;
  onChange: (value: Date) => void;
  disabled?: boolean;
  startHour?: number;
  endHour?: number;
  disablePastDates?: boolean;
  isInvalid?: boolean;
};

const DateTimePicker = ({
  value,
  onChange,
  disabled = false,
  startHour = 8,
  endHour = 18,
  disablePastDates = false,
  isInvalid = false,
}: DateTimePickerProps) => {
  const timeOptions = Array.from(
    { length: Math.max(1, (endHour - startHour) * 2) },
    (_, index) => {
      const hour = startHour + Math.floor(index / 2);
      const minute = index % 2 === 0 ? "00" : "30";
      return `${String(hour).padStart(2, "0")}:${minute}`;
    }
  );
  const setDate = (nextDate: Date | undefined) => {
    if (!nextDate) {
      return;
    }

    const nextValue = new Date(nextDate);
    nextValue.setHours(value.getHours(), value.getMinutes(), 0, 0);
    onChange(nextValue);
  };

  const setTime = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    const nextValue = new Date(value);
    nextValue.setHours(hours, minutes, 0, 0);
    onChange(nextValue);
  };

  const currentTime = `${String(value.getHours()).padStart(2, "0")}:${String(
    value.getMinutes()
  ).padStart(2, "0")}`;
  const selectedTime = timeOptions.includes(currentTime)
    ? currentTime
    : timeOptions[0];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const calendarDisabled = disablePastDates ? { before: today } : undefined;

  return (
    <div className="flex gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={`min-w-0 flex-1 justify-start font-normal ${
              isInvalid ? "!border-red-500 !text-red-500" : ""
            }`}
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-sky-700" />
            <span className="truncate">
              {format(value, "EEE, MMM d, yyyy")}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <Calendar
            mode="single"
            selected={value}
            onSelect={setDate}
            defaultMonth={value}
            disabled={calendarDisabled}
          />
        </PopoverContent>
      </Popover>
      <select
        aria-label="Service time"
        value={selectedTime}
        onChange={(event) => setTime(event.target.value)}
        disabled={disabled}
        className="h-9 w-24 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800 shadow-sm outline-none focus:border-sky-700 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
      >
        {timeOptions.map((time) => (
          <option key={time} value={time}>
            {time}
          </option>
        ))}
      </select>
    </div>
  );
};

export { DateTimePicker };
